import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { EnvName } from "./api";
import { showApiError } from "./api";
import { useApi } from "./useApi";
import { notifyBoardSync } from "./useBoardEvents";
import styles from "./transients.module.css";

type TransientRow = {
  id?: string;
  date: string;
  callsign: string;
  aircraft: string;
  origin: string;
  eta: string;
};

type Props = {
  env: EnvName;
  rows: TransientRow[];
};

export function TransientsPanel({ env, rows }: Props) {
  const api = useApi(env);
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    await qc.refetchQueries({ queryKey: ["dashboard", env] });
    notifyBoardSync(env);
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.deleteTransient(id),
    onSuccess: () => refresh(),
    onError: (err) => setError(showApiError(err) ?? "Remove failed"),
  });

  const clearMutation = useMutation({
    mutationFn: () => api.clearTransients(),
    onSuccess: () => refresh(),
    onError: (err) => setError(showApiError(err) ?? "Clear failed"),
  });

  const busy = removeMutation.isPending || clearMutation.isPending;

  async function clearAll() {
    if (!window.confirm("Remove all inbound PPR transients from the board?")) return;
    setError(null);
    clearMutation.mutate();
  }

  function removeOne(row: TransientRow) {
    if (!row.id) return;
    const label = row.callsign || row.aircraft || "this row";
    if (!window.confirm(`Remove transient ${label}?`)) return;
    setError(null);
    removeMutation.mutate(row.id);
  }

  return (
    <div className="card">
      <div className={styles.header}>
        <h2 className="section-title">Transients</h2>
        {rows.length > 1 ? (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={busy}
            onClick={() => void clearAll()}
          >
            Clear all
          </button>
        ) : null}
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Callsign</th>
            <th>Aircraft</th>
            <th>From</th>
            <th>ETA</th>
            <th className={styles.actions} />
          </tr>
        </thead>
        <tbody>
          {rows.map((t, index) => (
            <tr key={t.id ?? `${t.date}-${t.callsign}-${t.aircraft}-${index}`}>
              <td>{t.date}</td>
              <td>{t.callsign}</td>
              <td>{t.aircraft}</td>
              <td>{t.origin}</td>
              <td>{t.eta}</td>
              <td className={styles.actions}>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={busy || !t.id}
                  onClick={() => removeOne(t)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
